import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { API, IQuizSessionsResponse, IStringDynamicPrototype, SESSION_STATE } from 'src/types'
import { getHttp, postHttp } from 'src/api'
import { init } from '../facebookPixelService'
import { ACTION_APP, ga4EventApp, gaEventApp } from '../googleAnalyticsService'
import { SegmentAction, segmentTracking } from '../segment'
import { PATH_API, PATH_URL } from '../path'
import { ENV } from 'src/config/env'

import { useAppContext } from './App.context'
import { generateSessionID } from './helper'

export const SESSION_NAME_LOCAL = 'sessionID'

export const useAppBase = () => {
  const {
    appData,
    setSessionID,
    setStoreURLParameters,
    handleChangeAppData,
    setQuizSession,
    setQuizExperience,
    setExperiment,
  } = useAppContext()
  const { isLoading, countLoading, countLoadingTestimonial } = appData

  const location = useLocation()

  const currentParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    if (location.pathname.includes(PATH_URL.MICRO_WEDDING)) {
      handleChangeAppData({ isMicroWedding: true })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    const checkSessionId = async (sessionID: string): Promise<string> => {
      let newSessionID = sessionID
      try {
        const serviceabilityData = currentParams.get('serviceabilityData')
        if (serviceabilityData) handleChangeAppData({ isLoading: true })

        // Check if this is a Pinterest OAuth callback - if so, prioritize session ID from localStorage
        const isRedirectFromPinterest = currentParams.get('code') && window.location.pathname.includes('pinterest')
        if (isRedirectFromPinterest) {
          console.log('[Session ID] Detected Pinterest OAuth callback, prioritizing localStorage session ID')
          const storedSessionId = localStorage.getItem(SESSION_NAME_LOCAL)
          if (storedSessionId) {
            console.log('[Session ID] Using stored session ID from localStorage:', storedSessionId)
            newSessionID = storedSessionId
          }
        }

        // Use the full window.location.search if serviceabilityData exists, so that URL params are included when we create a session for serviceability
        const queryString = serviceabilityData ? window.location.search : ''

        const sessionResult: API<IQuizSessionsResponse> = await getHttp(
          `${PATH_API.getSessionId(newSessionID)}${queryString}`
        )
        localStorage.setItem(
          'tempPoppySessionData',
          JSON.stringify({
            ...JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}'),
            sessionId: newSessionID,
            skipServiceability: sessionResult.data.skipServiceability,
          })
        )
        switch (sessionResult.data.sessionState) {
          case SESSION_STATE.SUBMITTED:
            console.log('[Session ID] Session already submitted, generating new session ID')
            newSessionID = generateSessionID()
            if (sessionResult.data?.proposal?.hadProposal) {
              sessionStorage.setItem('proposalUrl', sessionResult.data.proposal.proposalUrl)
              window.location.href = PATH_URL.EXISTING_PROPOSAL_AFTER_SUBMITTED
            }
            // Create new session
            localStorage.setItem(
              'tempPoppySessionData',
              JSON.stringify({
                ...JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}'),
                skipServiceability: false,
              })
            )
            break
          default:
            break
        }
        setQuizExperience(sessionResult.data.quizExperience)
        setExperiment(sessionResult.data.experiment)
        setQuizSession(sessionResult.data)
      } catch (error) {
        console.log('[checkSessionId]', error)

        // If we're in Pinterest flow, try harder to recover the session ID
        const isRedirectFromPinterest = currentParams.get('code') && window.location.pathname.includes('pinterest')
        if (isRedirectFromPinterest) {
          console.log('[Session ID] Pinterest OAuth error - trying to recover session ID from localStorage')
          const storedSessionId = localStorage.getItem(SESSION_NAME_LOCAL)
          if (storedSessionId) {
            return storedSessionId
          }
        }
        newSessionID = generateSessionID()
        await postHttp(PATH_API.createSessionId(), { sessionId: newSessionID })
        setExperiment([])
        // history.push(PATH_URL.SWIPING)

        // Clear skip serviceability step
        localStorage.setItem(
          'tempPoppySessionData',
          JSON.stringify({
            ...JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}'),
            skipServiceability: false,
          })
        )
      } finally {
        handleChangeAppData({ isLoading: false })
      }

      return newSessionID
    }

    const initSessionID = async (searchParamsObject: IStringDynamicPrototype) => {
      // First check localStorage, then URL params
      let sessionID = localStorage.getItem(SESSION_NAME_LOCAL) || searchParamsObject[SESSION_NAME_LOCAL]

      if (!sessionID || searchParamsObject.quizData) {
        console.log('[Session ID] Generating new session ID', {
          hadExistingSession: !!sessionID,
          hasQuizData: !!searchParamsObject.quizData,
        })
        sessionID = generateSessionID()
        setExperiment([])
        // history.push(PATH_URL.SWIPING)
      } else {
        console.log('[Session ID] Using existing session ID', sessionID)
        sessionID = await checkSessionId(sessionID)
      }

      localStorage.setItem(SESSION_NAME_LOCAL, sessionID)
      setSessionID(sessionID)
    }
    const handleStoreURL = () => {
      let searchParamsObject: IStringDynamicPrototype = {}
      if (location.search) {
        searchParamsObject = Object.fromEntries(new URLSearchParams(location.search))
        setStoreURLParameters(searchParamsObject)
      }
      return searchParamsObject
    }

    const checkRunInitApp = () => {
      const urlDontCheck = [
        PATH_URL.YOUR_POPPY_PROPOSAL,
        PATH_URL.SCHEDULE_YOUR_CONSULTATION,
        PATH_URL.CALL_SCHEDULED,
        PATH_URL.$500_CUSTOMER,
        '/moodboard/',
        '/schedule-a-call/',
        '/post-moodboard/',
      ]
      const pathCheck = window.location.pathname
      return !urlDontCheck.some((e) => pathCheck.includes(e))
    }

    const initApp = async () => {
      handleChangeAppData({ isLoading: true })
      const searchParamsObject = handleStoreURL()
      await initSessionID(searchParamsObject)
      handleChangeAppData({ isLoading: false })
    }

    if (checkRunInitApp()) {
      initApp()
    } else {
      handleChangeAppData({ isLoading: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    init()
    const params = new URLSearchParams(window.location.search)
    // don't fire analytics on quiz load when returning from pinterest oauth or when importing pins
    //    this is kind of ugly on the import pins part, but for whatever reason we do a full page load there...
    if (!params.has('code') && !window.location.href.includes('import-pins')) {
      gaEventApp(ACTION_APP.QUIZ_LOADED)
      ga4EventApp(ACTION_APP.QUIZ_LOADED)

      // Track Help Center → Quiz attribution
      const referrer = document.referrer
      if (referrer && referrer.includes('/help/')) {
        try {
          const referrerPath = new URL(referrer).pathname
          ga4EventApp(ACTION_APP.QUIZ_FROM_HELP_CENTER, {
            referring_article: referrerPath,
            referrer_url: referrer,
          })
        } catch (e) {
          // Ignore malformed referrer URLs
        }
      }

      segmentTracking(
        SegmentAction.SQ_QUIZ_LOADED,
        currentParams.has('quizData') ? { x4: 'skipped_details' } : { x4: 'disabled' }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isLoading: isLoading || (countLoading || 0) > 0,
    isLoadingTestimonial: (countLoadingTestimonial || 0) > 0,
  }
}
